import { useEffect, useRef, useState } from 'react';
import ValidatedInput from '../../validations/ValidatedInput';
import { bannerApi } from './api';
import './BannersScreen.css';

function FileDrop({ file, preview, onChange }) {
  const inputRef = useRef(null);
  return (
    <button type="button" className={`bn-drop${file || preview ? ' is-filled' : ''}`} onClick={() => inputRef.current?.click()}>
      <span className="bn-drop__label">Banner image</span>
      {preview ? (
        <span className="bn-drop__preview"><img src={preview} alt="" /></span>
      ) : (
        <span className="bn-drop__icon" aria-hidden>↑</span>
      )}
      <span className="bn-drop__meta">{file?.name || 'Required — JPG / PNG / WEBP'}</span>
      <input ref={inputRef} type="file" accept="image/*" hidden required={!file} onChange={(e) => onChange(e.target.files?.[0] || null)} />
    </button>
  );
}

export default function BannersScreen({ onMessage }) {
  const flash = (t) => onMessage?.(t);
  const [banners, setBanners] = useState([]);
  const [bannerFile, setBannerFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [bannerForm, setBannerForm] = useState({
    title: '',
    subtitle: '',
    ctaText: '',
    ctaLink: '/products',
  });

  const refresh = async () => {
    const b = await bannerApi.adminAll().catch(() => ({ data: { data: [] } }));
    setBanners(b.data.data || []);
  };

  useEffect(() => { refresh().catch(() => flash('Could not load banners')); }, []);

  useEffect(() => {
    if (bannerFile) {
      const url = URL.createObjectURL(bannerFile);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    if (editingId) {
      const item = banners.find((b) => b._id === editingId);
      setPreview(item?.image || '');
      return undefined;
    }
    setPreview('');
    return undefined;
  }, [bannerFile, editingId, banners]);

  const resetForm = () => {
    setBannerForm({ title: '', subtitle: '', ctaText: '', ctaLink: '/products' });
    setBannerFile(null);
    setEditingId(null);
  };

  const saveBanner = async (e) => {
    e.preventDefault();
    if (!editingId && !bannerFile) { flash('Please choose a banner image'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(bannerForm).forEach(([k, v]) => fd.append(k, v));
      if (bannerFile) fd.append('image', bannerFile);
      if (editingId) {
        await bannerApi.update(editingId, fd);
        flash('Banner updated');
      } else {
        await bannerApi.create(fd);
        flash('Banner uploaded');
      }
      resetForm();
      await refresh();
    } catch (err) {
      flash(err.response?.data?.message || 'Banner failed');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (b) => {
    setEditingId(b._id);
    setBannerForm({
      title: b.title || '',
      subtitle: b.subtitle || '',
      ctaText: b.ctaText || '',
      ctaLink: b.ctaLink || '/products',
    });
    setBannerFile(null);
  };

  return (
    <div className="bn-screen">
      <form className="bn-screen__panel" onSubmit={saveBanner}>
        <p className="bn-screen__label">{editingId ? 'Edit banner' : 'New banner'}</p>
        <label className="bn-field"><span>Title <em>(optional)</em></span>
          <ValidatedInput kind="text" name="title" value={bannerForm.title} onChange={(e) => setBannerForm((f) => ({ ...f, title: e.target.value }))} placeholder="Leave empty for image-only" />
        </label>
        <label className="bn-field"><span>Subtitle <em>(optional)</em></span>
          <ValidatedInput kind="textarea" name="subtitle" rows={2} value={bannerForm.subtitle} onChange={(e) => setBannerForm((f) => ({ ...f, subtitle: e.target.value }))} placeholder="Optional" />
        </label>
        <div className="bn-field-row">
          <label className="bn-field"><span>CTA text <em>(optional)</em></span>
            <ValidatedInput kind="text" name="ctaText" value={bannerForm.ctaText} onChange={(e) => setBannerForm((f) => ({ ...f, ctaText: e.target.value }))} placeholder="Optional" />
          </label>
          <label className="bn-field"><span>CTA link</span>
            <ValidatedInput kind="text" name="ctaLink" value={bannerForm.ctaLink} onChange={(e) => setBannerForm((f) => ({ ...f, ctaLink: e.target.value }))} />
          </label>
        </div>
        <FileDrop file={bannerFile} preview={preview} onChange={setBannerFile} />
        <div className="bn-form-actions">
          {editingId && (
            <button type="button" className="bn-cancel" onClick={resetForm}>Cancel</button>
          )}
          <button type="submit" className="bn-save" disabled={saving}>
            {saving ? 'Saving…' : editingId ? 'Update banner' : 'Upload banner'}
          </button>
        </div>
      </form>

      <aside className="bn-screen__aside">
        <div className="bn-preview-block">
          <p className="bn-screen__label">Live preview</p>
          <div className="bn-preview" style={preview ? { backgroundImage: `url(${preview})` } : undefined}>
            <div className="bn-preview__copy">
              <strong>{bannerForm.title || 'Title'}</strong>
              <span>{bannerForm.subtitle || 'Subtitle'}</span>
              <em>{bannerForm.ctaText || 'Shop Now'}</em>
            </div>
          </div>
        </div>
        <div className="bn-list-block">
          <div className="bn-list-head"><p className="bn-screen__label">Uploaded</p><span>{banners.length}</span></div>
          <div className="bn-list">
            {banners.length === 0 && <p className="bn-empty">No banners yet.</p>}
            {banners.map((b) => (
              <article key={b._id} className={`bn-card${editingId === b._id ? ' is-editing' : ''}`}>
                <div className="bn-card__media" style={{ backgroundImage: `url(${b.image})` }} />
                <div className="bn-card__body">
                  <strong>{b.title}</strong>
                  <span>{b.subtitle}</span>
                </div>
                <div className="bn-card__actions">
                  <button type="button" className="bn-card__edit" onClick={() => startEdit(b)}>Edit</button>
                  <button type="button" className="bn-card__remove" onClick={async () => { await bannerApi.remove(b._id); if (editingId === b._id) resetForm(); refresh(); }}>Delete</button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </aside>
    </div>
  );
}
